import { callable } from './callable.js'
import { uploadWorkspaceFiles } from './storageHubService.js'

export const ALLOWED_REACTION_EMOJIS = ['👍', '❤️', '👏', '😂', '😮', '🙏'] as const
export type AllowedReactionEmoji = (typeof ALLOWED_REACTION_EMOJIS)[number]

export interface PostEduAnnouncementInput {
  workspaceId: string
  target: {
    nodeIds?: string[]
    allClasses?: boolean
  }
  kind: 'TEACHING' | 'TRAINING'
  title: string
  body: string
  fileIds?: string[]
  requestId: string
}

export interface EditEduAnnouncementInput {
  workspaceId: string
  announcementId: string
  title: string
  body: string
  fileIds?: string[]
}

export interface DeleteEduAnnouncementInput {
  workspaceId: string
  announcementId: string
}

export interface ListEduAnnouncementsInput {
  workspaceId: string
  nodeId?: string
  cursor?: string
}

export interface ReactEduAnnouncementInput {
  workspaceId: string
  announcementId: string
  learnerId: string
  emoji: string
}

export interface MarkEduAnnouncementReadInput {
  workspaceId: string
  announcementId: string
  learnerId: string
}

export interface EduAnnouncementAttachment {
  id?: string
  fileId?: string
  name: string
  size?: number
  contentType?: string
  downloadUrl?: string
}

export interface EduAnnouncement {
  id: string
  workspaceId: string
  nodeIds: string[]
  allClasses: boolean
  kind: 'TEACHING' | 'TRAINING'
  title: string
  body: string
  attachments: EduAnnouncementAttachment[]
  createdBy: string
  createdByName: string
  createdAt: unknown
  editedAt: unknown | null
  deletedAt: unknown | null
  reactionCounts: Record<string, number>
  readCount: number
  requestId?: string | null
  visible?: boolean
  whatsappSend?: {
    queued: number
    skipped: number
    at: unknown
    by: string
  } | null
}

export type EduAnnouncementItem = EduAnnouncement

export interface PostEduAnnouncementResult {
  success: boolean
  announcement: EduAnnouncement
  created: boolean
}

export interface ListEduAnnouncementsResult {
  announcements: EduAnnouncement[]
  nextCursor: string | null
}

export interface ReactEduAnnouncementResult {
  success: boolean
  reacted: boolean
  reactionCounts: Record<string, number>
}

export interface MarkEduAnnouncementReadResult {
  success: boolean
  readCount: number
}

export function generateEduRequestId(prefix: string = 'req'): string {
  const rand = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10)
  return `${prefix}_${Date.now()}_${rand}`.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64)
}

export async function postEduAnnouncement(
  input: PostEduAnnouncementInput,
): Promise<PostEduAnnouncementResult> {
  return callable('postEduAnnouncement', input)
}

export async function editEduAnnouncement(
  input: EditEduAnnouncementInput,
): Promise<{ success: boolean; announcement: EduAnnouncement }> {
  return callable('editEduAnnouncement', input)
}

export async function deleteEduAnnouncement(
  input: DeleteEduAnnouncementInput,
): Promise<{ success: boolean }> {
  return callable('deleteEduAnnouncement', input)
}

export async function listEduAnnouncements(
  input: ListEduAnnouncementsInput,
): Promise<ListEduAnnouncementsResult> {
  return callable('listEduAnnouncements', input)
}

export async function reactEduAnnouncement(
  input: ReactEduAnnouncementInput,
): Promise<ReactEduAnnouncementResult> {
  return callable('reactEduAnnouncement', input)
}

export async function markEduAnnouncementRead(
  input: MarkEduAnnouncementReadInput,
): Promise<MarkEduAnnouncementReadResult> {
  return callable('markEduAnnouncementRead', input)
}

export interface ListFamilyAnnouncementsInput {
  workspaceId: string
  learnerId: string
  cursor?: string
}

export interface FamilyAnnouncementItem extends EduAnnouncement {
  myReactions: string[]
  readByMe: boolean
}

export interface ListFamilyAnnouncementsResult {
  announcements: FamilyAnnouncementItem[]
  nextCursor: string | null
}

export async function listFamilyAnnouncements(
  input: ListFamilyAnnouncementsInput,
): Promise<ListFamilyAnnouncementsResult> {
  return callable('listFamilyAnnouncements', input)
}

export interface UploadAnnouncementFilesInput {
  workspaceId: string
  requestId: string
  files: File[]
  onProgress?: (completed: number, total: number, fileName: string) => void
}

export async function uploadAnnouncementFiles(
  input: UploadAnnouncementFilesInput,
): Promise<string[]> {
  if (input.files.length === 0) return []
  const results = await uploadWorkspaceFiles({
    workspaceId: input.workspaceId,
    files: input.files,
    visibility: 'PRIVATE',
    artifactType: 'EDU_ANNOUNCEMENT',
    artifactId: input.requestId,
    onProgress: input.onProgress,
  })
  const fileIds: string[] = []
  for (const res of results) {
    if (!res.fileId) {
      throw new Error('Upload completed without returning a file ID.')
    }
    fileIds.push(res.fileId)
  }
  return fileIds
}

export interface PreviewEduAnnouncementWhatsappInput {
  workspaceId: string
  announcementId: string
}

export interface PreviewEduAnnouncementWhatsappResult {
  recipients: number
  outsideCap: number
  creditsEstimate: number
  paused: boolean
  disabledReason?: 'WHATSAPP_OFF' | 'NOTIFY_OFF' | 'NO_RECIPIENTS'
}

export interface SendEduAnnouncementWhatsappInput {
  workspaceId: string
  announcementId: string
  requestId?: string
}

export interface SendEduAnnouncementWhatsappResult {
  queued: number
  skipped: number
}

export async function previewEduAnnouncementWhatsapp(
  input: PreviewEduAnnouncementWhatsappInput,
): Promise<PreviewEduAnnouncementWhatsappResult> {
  return callable('previewEduAnnouncementWhatsapp', input)
}

export async function sendEduAnnouncementWhatsapp(
  input: SendEduAnnouncementWhatsappInput,
): Promise<SendEduAnnouncementWhatsappResult> {
  return callable('sendEduAnnouncementWhatsapp', input)
}
